import axios from "axios";
import { logDevDebug, logDevWarn } from "@/utils/devLogger";

type DecodedJwt = {
  name?: string;
  family_name?: string;
  given_name?: string;
  exp?: number;
};

function redirectToMicrosoft() {
  const nonce = Math.random().toString(16).substring(2, 13);

  const scopes = [
    "openid",
    "profile",
    "email",
    // Explicitly request Microsoft Graph so the access token is accepted by /userinfo
    "https://graph.microsoft.com/User.Read",
  ];

  const queryParams = [
    `client_id=${process.env.VUE_APP_OAUTH2_CLIENT_ID}`,
    `redirect_uri=${encodeURIComponent(
      process.env.VUE_APP_OAUTH2_REDIRECT_URL ?? ""
    )}`,
    // Include standard OIDC scopes so the access token is accepted by the /userinfo endpoint
    `scope=${encodeURIComponent(scopes.join(" "))}`,
    "response_type=token",
    "response_mode=fragment",
    `nonce=${nonce}`,
  ];

  window.location.href = `${
    process.env.VUE_APP_OAUTH2_URL
  }/authorize?${queryParams.join("&")}`;
}

function saveProfileSettings(jwtDecode: DecodedJwt) {
  const profile = {
    name: jwtDecode.name,
    family_name: jwtDecode.family_name,
    given_name: jwtDecode.given_name,
  };
  localStorage.profile = JSON.stringify(profile);
}

function decodeJwtToken(accessToken?: string): DecodedJwt | null {
  if (!accessToken) {
    return null;
  }

  const base64Url = accessToken.split(".")[1];
  if (!base64Url) {
    return null;
  }

  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(
        (character) =>
          `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`
      )
      .join("")
  );

  try {
    return JSON.parse(jsonPayload) as DecodedJwt;
  } catch (error) {
    logDevWarn("JWT payload could not be parsed", error);
    return null;
  }
}

export default {
  getHeader() {
    const rawAuth = localStorage.auth;
    const token = rawAuth ? JSON.parse(rawAuth) : {};
    const accessToken =
      typeof token?.access_token === "string" ? token.access_token : "";

    if (process.env.NODE_ENV !== "production") {
      logDevDebug("Auth header (sanitized)", {
        len: accessToken.length,
        preview: accessToken ? `${accessToken.substring(0, 12)}...` : "empty",
        hasDots: accessToken.includes("."),
      });
    }

    return {
      headers: {
        Accept: "*/*",
        Authorization: `Bearer ${accessToken}`,
      },
    };
  },
  async init() {
    if (process.env.NODE_ENV !== "production") {
      const devToken = process.env.VUE_APP_DEV_JWT;

      if (devToken) {
        localStorage.auth = JSON.stringify({
          access_token: devToken,
          expires_in: "10000",
          is_dev_token: true,
        });
      }
    }

    if (!localStorage.auth) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const token = hashParams.get("access_token");

      if (token) {
        localStorage.auth = JSON.stringify({
          access_token: token,
          expires_in: hashParams.get("expires_in"),
        });

        // clear hash params in the browser url
        window.history.pushState(
          "",
          document.title,
          `${window.location.origin}${window.location.pathname}`
        );
      } else {
        localStorage.clear();
        redirectToMicrosoft();

        return;
      }
    }

    const rawAuth = localStorage.auth;
    if (!rawAuth) {
      redirectToMicrosoft();
      return;
    }

    let authJson: any;
    try {
      authJson = JSON.parse(rawAuth);
    } catch (error) {
      logDevWarn("Auth payload could not be parsed", error);
      localStorage.clear();
      redirectToMicrosoft();
      return;
    }
    const isDevToken = authJson.is_dev_token === true;
    const jwtDecode = isDevToken
      ? {
        exp: Math.round(Date.now() / 1000) + 86400,
        name: "Mealorder Dev",
        family_name: "Dev",
        given_name: "Mealorder",
      }
      : decodeJwtToken(authJson.access_token);

    if (!jwtDecode) {
      localStorage.clear();
      redirectToMicrosoft();
      return;
    }

    saveProfileSettings(jwtDecode);

    const time = (jwtDecode.exp ?? 0) - Math.round(Date.now() / 1000);
    if (time <= 100) {
      localStorage.clear();
      redirectToMicrosoft();
      return;
    }

    if (!localStorage.user_image && !isDevToken) {
      void axios
        .get("https://graph.microsoft.com/v1.0/me/photo/$value", {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${authJson.access_token}`,
          },
        })
        .then((resp) => {
          const reader = new window.FileReader();
          reader.readAsDataURL(resp.data);
          reader.onload = () => {
            const imageDataUrl = reader.result;

            localStorage.user_image = imageDataUrl as string;
          };
        })
        .catch((error) => {
          logDevWarn("Profile image could not be loaded", error);
        });
    }
  },
};
